import { FC, useCallback } from 'react';
import { ResizablePanel, ResizablePanelGroup, Tabs, TabsContent, TabsList, TabsTrigger } from '@ibsys/kit';
import { ArrowLeftIcon } from 'lucide-react';

import { useTranslation } from 'react-i18next';
import { DialogDataSection } from '@/features/DialogDataSection';
import { DialogTranscriptSection } from '@/features/DialogTranscriptSection';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CONVERSATION_TAB_PARAM, useIsTextConversation } from '@/entities/conversations';
import { useTextAttributesPanel } from '@/entities/settings';
import { IconButton, useMainLayout } from '@/shared/ui';
import { DialogDataResizeHandle } from '../DialogDataResizeHandle';
import { useDialogDataVisibility } from '../model/useDialogDataVisibility';
import { useDialogsSectionRoute } from '../model/useDialogsSectionRoute';
import { useTranscriptPanelReveal } from '../model/useTranscriptPanelReveal';

type SessionDetailsProps = {
  id: number;
};

export const SessionDetails: FC<SessionDetailsProps> = ({ id }) => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get(CONVERSATION_TAB_PARAM) ?? undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMainLayout();
  const { t } = useTranslation(['dialogs', 'common']);
  const { listPath, buildRecordPath } = useDialogsSectionRoute();
  const { isDataHidden, handleDataResize } = useDialogDataVisibility();
  const isTextConversation = useIsTextConversation(id);
  const { isAttributesPanelOpen, setAttributesPanelOpen, toggleAttributesPanel } = useTextAttributesPanel();
  // На узком экране панель атрибутов — вкладка: у текстовой коммуникации со скрытой панелью
  // карточка открывается на переписке
  const panel = searchParams.get('panel') ?? (isTextConversation && !isAttributesPanelOpen ? 'transcript' : 'data');

  const handleBack = () => {
    // Источник перехода запоминается в state как полный путь возврата (дашборд,
    // выборка, список диалогов). Его нет — возвращаемся к списку текущего раздела.
    navigate(typeof location.state === 'string' ? location.state : listPath);
  };

  const handleTabChange = useCallback(
    (newTab: string) => {
      const params = new URLSearchParams(searchParams);
      params.set(CONVERSATION_TAB_PARAM, newTab);
      navigate(`${buildRecordPath(String(id))}?${params.toString()}`, { replace: true });
    },
    [id, navigate, searchParams, buildRecordPath],
  );

  const handlePanelChange = useCallback(
    (newPanel: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('panel', newPanel);
      navigate(`${buildRecordPath(String(id))}?${params.toString()}`, { replace: true });
    },
    [id, navigate, searchParams, buildRecordPath],
  );

  // Выбор вкладки у текстовой коммуникации запоминается так же, как кнопкой «Атрибуты» на широком экране.
  // Переход из вкладки «Вложения» к сообщению — не выбор: он переключает вкладку без запоминания
  const handlePanelSelect = useCallback(
    (newPanel: string) => {
      handlePanelChange(newPanel);
      if (isTextConversation) setAttributesPanelOpen(newPanel === 'data');
    },
    [handlePanelChange, isTextConversation, setAttributesPanelOpen],
  );

  const showTranscriptPanel = useCallback(() => handlePanelChange('transcript'), [handlePanelChange]);

  useTranscriptPanelReveal({
    isEnabled: isMobile,
    isTranscriptShown: panel === 'transcript',
    showTranscript: showTranscriptPanel,
  });

  if (!id) return <div />;

  if (isMobile) {
    return (
      <Tabs
        value={panel}
        onValueChange={handlePanelSelect}
        className="grid grid-rows-[auto_1fr] grid-cols-1 h-full gap-3 min-h-0"
      >
        <div className={'flex gap-2 items-center'}>
          <IconButton
            variant={'link'}
            className={'p-1'}
            label={t('common:back')}
            icon={<ArrowLeftIcon className={'size-5'} />}
            onClick={handleBack}
          />
          <TabsList>
            <TabsTrigger value="data">{t('dialogData')}</TabsTrigger>
            <TabsTrigger value="transcript">{t('dialog')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="data" className="overflow-hidden">
          <DialogDataSection
            dialogId={id}
            hideBackButton
            onBack={handleBack}
            activeTab={tab}
            onTabChange={handleTabChange}
          />
        </TabsContent>
        <TabsContent value="transcript" className="flex-1 overflow-hidden">
          <DialogTranscriptSection dialogId={id} showAudio isCollapsible={false} />
        </TabsContent>
      </Tabs>
    );
  }

  // У текстовой коммуникации панель атрибутов по умолчанию скрыта: переписка занимает её место
  if (isTextConversation && !isAttributesPanelOpen) {
    return (
      <div className="h-full min-h-0 p-1">
        <DialogTranscriptSection
          dialogId={id}
          showAudio={false}
          isCollapsible={false}
          isAttributesPanelOpen={false}
          onToggleAttributesPanel={toggleAttributesPanel}
          onBack={handleBack}
        />
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className={'gap-3 h-full'} storageName={'dialogs-page-advanced'}>
      <ResizablePanel className={'p-1'} id={'data'} onResize={handleDataResize}>
        <DialogDataSection dialogId={id} onBack={handleBack} activeTab={tab} onTabChange={handleTabChange} />
      </ResizablePanel>
      <DialogDataResizeHandle isDataHidden={isDataHidden} />
      <ResizablePanel className={'p-1'} id={'chat'} groupResizeBehavior={'preserve-relative-size'} defaultSize={'33%'}>
        <DialogTranscriptSection
          dialogId={id}
          showAudio={false}
          isCollapsible={false}
          isAttributesPanelOpen={isTextConversation ? isAttributesPanelOpen : undefined}
          onToggleAttributesPanel={isTextConversation ? toggleAttributesPanel : undefined}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
